import { isApiError } from '@/services/httpClient';

export type RetryAsyncOptions = {
  /** Attempts including the first try. Default 3. */
  attempts?: number;
  /** Delay before attempt index 1, 2, … (ms). Default [800, 2000]. */
  delaysMs?: number[];
  /** Retry only these HTTP statuses (and network failures). Default 502/503/504. */
  retryStatuses?: number[];
};

/**
 * Retry an async call on transient gateway/network failures.
 * Does not retry typical 4xx client errors.
 */
export async function retryAsync<T>(
  fn: () => Promise<T>,
  options: RetryAsyncOptions = {}
): Promise<T> {
  const attempts = options.attempts ?? 3;
  const delaysMs = options.delaysMs ?? [800, 2000];
  const retryStatuses = new Set(options.retryStatuses ?? [502, 503, 504]);

  let lastError: unknown;
  for (let i = 0; i < attempts; i++) {
    if (i > 0) {
      const delay = delaysMs[i - 1] ?? delaysMs[delaysMs.length - 1] ?? 1000;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (isApiError(error)) {
        if (!retryStatuses.has(error.response.status)) {
          throw error;
        }
      }
      // Network / unknown — keep retrying until attempts exhausted
    }
  }
  throw lastError;
}
